import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { Token } from './token';
export { Token };
import { tokenSchema } from '@schemas/business-objects/token';
import { ConditionWatcherFormArray } from './condition-watcher-form-group';
import { MessageListenerFormArray } from './message-listener-form-group';
import { MyUserTaskFormArray } from './my-user-task-form-group';
import {
  ProcessInstanceFormArray,
  ProcessInstanceFormGroup,
} from './process-instance-form-group';
import { ServiceTaskFormArray } from './service-task-form-group';
import { TenantFormGroup } from './tenant-form-group';
import { TimerFormArray } from './timer-form-group';
import { TokenHistoryFormArray } from './token-history-form-group';
import { TokenLinkFormArray } from './token-link-form-group';
import { UserTaskFormArray } from './user-task-form-group';
import type { PendingStateUpdate } from './pending-state-update';
import type { ProcessDesign } from './process-design';

export class TokenFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    referenceNumber: SchemaFormControl;
    activityId: SchemaFormControl;
    status: SchemaFormControl;
    errorMessage: SchemaFormControl;
    errorCode: SchemaFormControl;
    loopItems: SchemaFormControl;
    createdAt: SchemaFormControl;
    updatedAt: SchemaFormControl;
    processInstanceId: SchemaFormControl;
    processInstance: ProcessInstanceFormGroup;
    userTasks: UserTaskFormArray;
    serviceTasks: ServiceTaskFormArray;
    childProcessInstances: ProcessInstanceFormArray;
    tokenHistories: TokenHistoryFormArray;
    downstreamLinks: TokenLinkFormArray;
    upstreamLinks: TokenLinkFormArray;
    timers: TimerFormArray;
    messageListeners: MessageListenerFormArray;
    myUserTasks: MyUserTaskFormArray;
    conditionWatchers: ConditionWatcherFormArray;
    tenantId: SchemaFormControl;
    tenant: TenantFormGroup;
  };

  declare value: Partial<Token>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(tokenSchema, Token, data, options);
  }

  get object(): Token {
    return new Token(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'processInstance':
        return new ProcessInstanceFormGroup();
      case 'userTasks':
        return new UserTaskFormArray();
      case 'serviceTasks':
        return new ServiceTaskFormArray();
      case 'childProcessInstances':
        return new ProcessInstanceFormArray();
      case 'tokenHistories':
        return new TokenHistoryFormArray();
      case 'downstreamLinks':
        return new TokenLinkFormArray();
      case 'upstreamLinks':
        return new TokenLinkFormArray();
      case 'timers':
        return new TimerFormArray();
      case 'messageListeners':
        return new MessageListenerFormArray();
      case 'myUserTasks':
        return new MyUserTaskFormArray();
      case 'conditionWatchers':
        return new ConditionWatcherFormArray();
      case 'tenant':
        return new TenantFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<Token[]> {
    return Token.find(filter);
  }

  static async findOne(filter?: any): Promise<Token | null> {
    return Token.findOne(filter);
  }

  static async findById(id: string, filter?: any): Promise<Token> {
    return Token.findById(id, filter);
  }

  static async create(data: Partial<Token>): Promise<Token> {
    return Token.create(data as any);
  }

  static async updateById(id: string, updates: Partial<Token>): Promise<Token> {
    return Token.updateById(id, updates as any);
  }

  static async deleteById(id: string): Promise<boolean> {
    return Token.deleteById(id);
  }

  async move(options: any): Promise<any> {
    const instance = new Token(this.getRawValue());
    return instance.move(options);
  }
}

export class TokenFormArray extends PersistedFormArray {
  declare controls: TokenFormGroup[];

  readonly entityName = 'Token' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(tokenSchema, Token, options);
  }

  protected override createItemGroup() {
    return new TokenFormGroup();
  }

  get array(): Token[] {
    return this.controls.map((group: TokenFormGroup) => group.object);
  }
}

export class TokenPersistedArray extends PersistedArray<Token> {
  readonly entityName = 'Token' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Token, options);
  }
}
